import React from 'react';
import TagMultiSelect from './ui/TagMultiSelect';
import SearchableSelect from './ui/SearchableSelect';
import RichTextEditor from './ui/RichTextEditor';
import { ParsedVendor } from '../data/vendorData';
import { BudgetCategory } from '../types/ui/Budget';
import { StoredEvent } from '../services/EventsService';

// The canonical field set for creating a VendorQuote — shared by
// QuotesTracker's "+ New Quote" form and BudgetTracker's "Add Vendor" form,
// so a quote started from either page collects the same data.
export interface QuoteFormValues {
  vendorSearch: string;
  selectedVendorId: string;
  categoryId: string;
  eventIds: string[];
  dateReceived: string;
  notes: string;
}

interface QuoteFormFieldsProps {
  vendors: ParsedVendor[];
  categories: BudgetCategory[];
  events: StoredEvent[];
  values: QuoteFormValues;
  onChange: (updates: Partial<QuoteFormValues>) => void;
  vendorError?: string;
}

export const QuoteFormFields: React.FC<QuoteFormFieldsProps> = ({
  vendors, categories, events, values, onChange, vendorError,
}) => {
  return (
    <>
      <div className="form-row">
        <div className="form-group qt-vendor-search-wrap">
          <label>Vendor <span className="form-required">*</span></label>
          <SearchableSelect
            value={values.selectedVendorId || values.vendorSearch}
            onChange={id => {
              const v = vendors.find(vv => vv.id === id);
              onChange({ selectedVendorId: id, vendorSearch: v?.name ?? id });
            }}
            options={vendors.map(v => ({ value: v.id, label: v.name }))}
            onCreateNew={name => onChange({ selectedVendorId: '', vendorSearch: name })}
            placeholder="Search vendors or type a name…"
          />
          {vendorError && <span className="field-error">{vendorError}</span>}
        </div>
        <div className="form-group">
          <label>Budget Category</label>
          <SearchableSelect
            value={values.categoryId}
            onChange={id => onChange({ categoryId: id })}
            options={categories.map(c => ({ value: c.id, label: c.name }))}
            placeholder="Select a category…"
          />
        </div>
      </div>
      <div className="form-row">
        <div className="form-group">
          <label>Events <span className="form-label-hint">(optional)</span></label>
          <TagMultiSelect
            values={values.eventIds.map(eid => events.find(e => e.id === eid)?.title ?? eid)}
            options={events.map(ev => ev.title)}
            onChange={titles => {
              const ids = titles.map(t => events.find(ev => ev.title === t)?.id ?? t);
              onChange({ eventIds: ids });
            }}
            placeholder="All events"
            allowCreate={false}
          />
        </div>
        <div className="form-group">
          <label>Date Received</label>
          <input type="date" value={values.dateReceived} onChange={e => onChange({ dateReceived: e.target.value })} />
        </div>
      </div>
      <RichTextEditor
        label="Notes"
        value={values.notes}
        onChange={val => onChange({ notes: val })}
        placeholder="Anything worth remembering about this bid… (optional)"
        rows={2}
      />
    </>
  );
};

export default QuoteFormFields;
